import React, { useState } from 'react';
import cn from 'classnames';
import { useDropzone } from 'react-dropzone';
import styles from './image-upload.module.css';
import Button from '../../../components/forms/Buttons';

const imageTypesAccepted = 'image/jpeg, image/png';
const maxImageSizeInBytes = 1000000; // express-fileupload default.

type ImageUploadProps = {
  onImageAccepted: (file: File) => void;
  onImageRejected: () => void;
  file?: File;
};

interface FileError {
  name: string;
  messages: string[];
}

const FileAcceptedMessage: React.FC<{ filename: string }> = ({ filename }) => (
  <p>
    <i
      className={cn(
        'glyphicon',
        'glyphicon-ok',
        styles.imageAcceptedStatusIcon,
      )}
    />
    {filename} uploaded
  </p>
);

const FileRejectedMessage: React.FC<{
  filename: string;
  errorMessages: string[];
}> = ({ filename, errorMessages }) => (
  <p>
    <i
      className={cn(
        'glyphicon',
        'glyphicon-remove',
        styles.imageRejectedStatusIcon,
      )}
    />
    {filename} rejected: {errorMessages.join(', ')}
  </p>
);

export const ImageUpload: React.FC<ImageUploadProps> = ({
  onImageAccepted,
  onImageRejected,
  file,
}) => {
  const [errors, setErrors] = useState<FileError[]>([]);
  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept: imageTypesAccepted,
    maxSize: maxImageSizeInBytes,
    multiple: false,
    onDropAccepted: acceptedFiles => {
      onImageAccepted(acceptedFiles[0]);
      setErrors([]);
    },
    onDropRejected: fileRejections => {
      onImageRejected();
      setErrors(
        fileRejections.map(fileRejection => ({
          name: fileRejection.file.name,
          messages: fileRejection.errors.map(error => error.message),
        })),
      );
    },
  });

  const imageRejected = errors.length > 0;
  const isDraggingOver = isDragAccept || isDragReject;

  return (
    <div
      {...getRootProps({
        className: cn(
          styles.uploadTarget,
          { [styles.imageWillBeAccepted]: isDragAccept },
          { [styles.imageWillBeRejected]: isDragReject },
          { [styles.imageAccepted]: !isDraggingOver && file },
          { [styles.imageRejected]: !isDraggingOver && imageRejected },
        ),
      })}
    >
      <input {...getInputProps()} />
      {file && <FileAcceptedMessage filename={file.name} />}
      {imageRejected && errors[0] && (
        <FileRejectedMessage
          filename={errors[0].name}
          errorMessages={errors[0].messages}
        />
      )}
      <Button id="uploadImageButton" onClick={() => {}}>
        UPLOAD NEW IMAGE
      </Button>
    </div>
  );
};
